<template>
  <div class="flex flex-row gap-y-1" v-show="isShow">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
      <label
        v-if="label"
        :for="fieldName"
        :class="labelClass"
        class="relative flex items-center gap-x-2"
      >
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex flex-col justify-start items-start gap-2">
        <button
          class="relative rounded-md bg-primary/80 hover:bg-primary px-3 py-1.5 flex items-center gap-x-2"
          @click.prevent="() => (isShowEditPanel = !isShowEditPanel)"
        >
          <div
            class="flex justify-center items-center h-full bg-black/10 absolute top-0 start-0 p-2 rounded-s"
          >
            <FontAwesomeIcon :icon="faPlusSquare" class="w-3 text-white" />
          </div>
          <span class="ms-5 ltr:font-sans tracking-wide flex-1 text-white text-sm pb-[1px]">
            {{ $t(props.component_info.add_button_label) }}
          </span>
        </button>

        <DataTable :data-set="dataSet" v-if="dataSet" class="w-full">
          <template #headerCell="{ label }">
            <span v-if="label == 'actions'"></span>
          </template>
          <template #cell="{ columnName, columnValue, rowIndex }">
            <span v-if="needHtmlFormatColumn.includes(columnName)" v-html="columnValue"></span>
            <span v-else>{{ columnValue }}</span>
            <div v-if="columnName == 'actions'" class="w-full h-full flex gap-1">
              <button
                class="rounded p-1 bg-cyan-400/80 hover:bg-cyan-400"
                @click.prevent="editRawHandle(rowIndex)"
              >
                <i class="fa fa-edit w-3 text-white" />
              </button>
              <button
                class="rounded p-1 bg-primary/80 hover:bg-primary"
                @click.prevent="removeRawHandle(rowIndex)"
              >
                <i class="fa fa-trash w-3 text-white" />
              </button>
            </div>
          </template>
        </DataTable>
        <div v-show="isShowEditPanel" class="flex flex-col gap-y-2 w-full">
          <template v-for="field in processedNestedObj" :key="field.field_name">
            <component
              v-if="canShow(field.showRules, field.device_type, field)"
              :is="findComponent(field.component)"
              :field-name="field.field_name"
              :dependent-on="field.dependent_on"
              :on-change="field.on_change"
              :on-blur="field.on_blur"
              :default-value="formStore.draft?.request?.[field.field_name] || field.default_value"
              :is-disabled="field.disabled"
              :server-date-format="field.server_date_format"
              :min-date="field.min_date"
              :max-date="field.max_date"
              :prefixDisabled="field.prefixDisabled"
              :showRules="field.showRules"
              :radioOptions="field.radio_options"
              :sequenceOptions="field.sequence_options"
              :layout="section?.form_style?.web?.layout"
              v-bind="{ ...field, ...layoutClasses(section?.form_style?.web?.layout) }"
            ></component>
          </template>
          <div class="flex justify-end items-center gap-2">
            <button
              class="relative rounded-md bg-gradient-to-br from-linear-s to-linear-e px-3 py-1.5 flex items-center gap-x-2"
              @click.prevent="cancelEditHandle"
            >
              <div
                class="flex justify-center items-center h-full bg-black/10 absolute top-0 start-0 p-2 rounded-s"
              >
                <i class="fa fa-remove w-3 text-white" />
              </div>
              <span class="ms-5 ltr:font-sans tracking-wide flex-1 text-white text-sm pb-[1px]">
                {{ $t('cancel') }}
              </span>
            </button>

            <button
              class="relative rounded-md bg-gradient-to-br from-linear-s to-linear-e px-3 py-1.5 flex items-center gap-x-2"
              @click.prevent="addOrUpdateRow"
            >
              <div
                class="flex justify-center items-center h-full bg-black/10 absolute top-0 start-0 p-2 rounded-s"
              >
                <i class="fa fa-save w-3 text-white" />
              </div>
              <span class="ms-5 ltr:font-sans tracking-wide flex-1 text-white text-sm pb-[1px]">
                {{ isOnAddMode ? $t('add') : $t('update') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, toRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormStore } from '@/stores/form'
import useVisible from '@/composables/useVisible'
import { useRoute } from 'vue-router'
import DataTable from '@/components/DataTable/index.vue'
import HintTippy from '@/components/Form/HintTyppy.vue'
import { notify } from 'notiwind'

import CommonButton from '@/components/Form/CommonButton.vue'
import AutocompleteDropdown from '@/components/Form/AutocompleteDropdown.vue'
import CheckboxTicker from '@/components/Form/CheckboxTicker.vue'
import TextRenderer from '@/components/Form/TextRenderer.vue'
import ComboText from '@/components/Form/ComboText.vue'
import DropdownSearch from '@/components/Form/DropdownSearch.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import InfoLabel from '@/components/Form/InfoLabel.vue'
import radio_group from '@/components/Form/RadioGroup.vue'
import RecaptchaTicker from '@/components/Form/RecaptchaTicker.vue'
import SpacerComponent from '@/components/Form/SpacerVue.vue'
import TextInput from '@/components/Form/TextInput.vue'
import TwowayDropdown from '@/components/Form/TwowayDropdown.vue'
import VisaFileNumber from '@/components/Form/VisaFileNumber.vue'
import DatepickerCalendar from '@/components/Form/DatepickerCalendar.vue'
import TimePicker from '@/components/Form/TimePicker.vue'
import DateTimePicker from '@/components/Form/DateTimePicker.vue'

import { faPlusSquare } from '@fortawesome/free-solid-svg-icons'

import { useFieldArray } from 'vee-validate'
import lodashGet from 'lodash/get'

const props = defineProps({
  component_info: String,
  editFieldNamePrefix: {
    type: String,
    required: true
  },
  templateSchema: {
    type: Array,
    required: true
  },
  visibleColumns: {
    type: Array,
    required: true
  },
  layout: {
    type: String,
    default: 'one-column'
  },
  hint: String,
  labelValue: String,
  radioOptions: {
    type: Array,
    required: true
  },
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  modelValue: {
    type: [String, Number]
  },
  rules: Array,
  showRules: Object,
  defaultValue: String,
  direction: {
    type: String,
    default: 'flex-row md:items-start '
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  dependentOn: String,
  sequenceOptions: Array,
  debounceTime: {
    type: Number,
    default: () => 0
  }
})

const formStore = useFormStore()
const i18n = useI18n()

const isShowEditPanel = ref(true)
const isOnAddMode = ref(true)
const currentEditRowIndex = ref()
const needHtmlFormatColumn = ref([])

const { replace, update, remove, push, fields } = useFieldArray(props.fieldName)

// format the data table column that need to be displayed in html format
props?.visibleColumns?.forEach((column) => {
  if (Array.isArray(column.value)) {
    needHtmlFormatColumn.value.push(i18n.t(column.label))
  }
})

const dataSet = computed(() => {
  if (!formStore.form.values[props.fieldName]) return

  return formStore.form.values[props.fieldName].map((item) => {
    const resMap = {}

    props.visibleColumns.map((column) => {
      if (Array.isArray(column.value)) {
        return (resMap[i18n.t(column.label)] = column.value
          .map((nestedColumn) => {
            return lodashGet(item, nestedColumn)
          })
          .join('<br\>'))
      }
      return (resMap[i18n.t(column.label)] = lodashGet(item, column.value))
    })

    resMap.actions = ''
    return resMap
  })
})

const processBlurUnderNestedField = (on_blur) => {
  if (on_blur?.api?.translate_to) {
    on_blur.api.translate_to = generateEditFieldName(on_blur.api.translate_to)
  }
  return on_blur
}
const generateEditFieldName = (fieldName) => {
  // Avoid processing name of fields many times.
  if (fieldName.indexOf(props.editFieldNamePrefix) >= 0) {
    return fieldName
  }
  return `${props.editFieldNamePrefix}${props.fieldName}.${fieldName}`
}
const cancelEditHandle = () => {
  isOnAddMode.value = true
  formStore.form.setFieldValue(`${props.editFieldNamePrefix}${props.fieldName}`, null)
}
// Because the nested fields are not in the form store, we need to validate them manually
const validateRequiredField = (filledRow) => {
  const errorMessage = {}
  props.templateSchema
    .filter((x) => x.device_type != 'mobile')
    .map((field) => {
      const requiredRule = field.rules?.find((r) => r.name == 'required' || 'required_ui')
      if (requiredRule) {
        let needNotValid = field.showRules
          ? findShowRule(
              field.showRules.any || field.showRules.all,
              field.showRules.any ? 'any' : 'all',
              getBindObj(route.params.serviceId)
            )
          : true

        if (!lodashGet(filledRow, field.field_name) && needNotValid) {
          errorMessage[i18n.t(field.label)] = requiredRule.message
        }
      }
    })
  if (Object.keys(errorMessage).length === 0) return true

  const messages = Object.entries(errorMessage)
    .map(([key, value]) => `${key}: ${i18n.t(value)}`)
    .join('<br/>')
  notify(
    {
      group: 'error',
      title: 'error',
      text: messages
    },
    6000
  )
}

const addOrUpdateRow = () => {
  const needUpdateRow = formStore.form.values[`${props.editFieldNamePrefix}${props.fieldName}`]
  if (!validateRequiredField(needUpdateRow)) {
    return
  }
  if (isOnAddMode.value) {
    push({ ...needUpdateRow })
  } else {
    update(currentEditRowIndex.value, needUpdateRow)
  }

  formStore.form.setFieldValue(`${props.editFieldNamePrefix}${props.fieldName}`, null)
  isOnAddMode.value = true
}

const removeRawHandle = (rowIndex) => {
  remove(rowIndex)
  isOnAddMode.value = true
}
const editRawHandle = (rowIndex) => {
  currentEditRowIndex.value = rowIndex
  const nowRow = formStore.form.values[props.fieldName][rowIndex]
  isOnAddMode.value = false
  formStore.form.setFieldValue(`${props.editFieldNamePrefix}${props.fieldName}`, nowRow)
}

const processedNestedObj = computed(() => {
  const res = props?.templateSchema?.map((field) => {
    return {
      ...field,
      field_name: field.field_name && generateEditFieldName(field.field_name),
      dependent_on: field.dependent_on && generateEditFieldName(field.dependent_on),
      on_blur: field.on_blur && processBlurUnderNestedField(field.on_blur)
    }
  })
  return res
})

const { findShowRule, getBindObj } = useVisible()
const route = useRoute()

const isShow = computed(() => {
  if (props.showRules) {
    return findShowRule(
      props.showRules.any || props.showRules.all,
      props.showRules.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )
  } else {
    return true
  }
})
const t9nLabel = computed(() => i18n.t(props.label))

const canShow = (showRules, deviceType, item) => {
  if (deviceType === 'mobile') return false
  if (!showRules) return true
  return findShowRule(
    showRules.any || showRules.all,
    showRules.any ? 'any' : 'all',
    getBindObj(route.params.serviceId)
  )
}
const layoutClasses = (layout) => {
  if (layout === 'two-column') {
    return {
      bodyClass: 'flex flex-wrap gap-y-2',
      class: '!w-1/2 px-2',
      direction: 'flex-col',
      labelClass: '!w-full',
      showErrorSpacer: false
    }
  } else {
    return {
      bodyClass: 'flex flex-col gap-y-2',
      showErrorSpacer: false
    }
  }
}

const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))

const findComponent = (component) => {
  switch (component) {
    case 'ComboText':
      return ComboText
    case 'AutocompleteDropdown':
      return AutocompleteDropdown
    case 'DropdownSearch':
      return DropdownSearch
    case 'InfoBar':
      return InfoBar
    case 'TextInput':
      return TextInput
    case 'TwowayDropdown':
      return TwowayDropdown
    case 'DatepickerCalendar':
      return DatepickerCalendar
    case 'TimePicker':
      return TimePicker
    case 'DateTimePicker':
      return DateTimePicker
    case 'RecaptchaTicker':
      return RecaptchaTicker
    case 'InfoLabel':
      return InfoLabel
    case 'RadioGroup':
    case 'radio_group':
      return radio_group
    case 'VisaFileNumber':
      return VisaFileNumber
    case 'CommonButton':
      return CommonButton
    case 'Spacer':
      return SpacerComponent
    case 'TextRender':
      return TextRenderer
    case 'CheckBox':
      return CheckboxTicker
    default:
      return component
  }
}
</script>
<script>
export default {
  name: 'ReactiveFormGroup'
}
</script>

<style lang="scss" scoped></style>
